import React, {
  useState,
  useEffect,
  useContext,
  Dispatch,
  SetStateAction,
} from "react";
import { ChakraProvider } from "@chakra-ui/react";
import { LocaleProvider } from "./providers/LocaleProvider";
import { BrowserRouter } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "react-query";
import { ReactQueryDevtools } from "react-query/devtools";
import "./App.css";
import "react-datepicker/dist/react-datepicker.css";
import "./richTextStyles.css";
import MediaBox from "src/providers/MediaBox";
import { Provider as ReduxProvider, useDispatch } from "react-redux";
import reduxStore from "src/store";
import { toggleIsBtn, toggleBlinkValue, setMessage, startDateValue } from "src/store/media";
import CookieChecker from "./components/CookieChecker/CookieChecker";
import CrashWrapper from "./views/Crash/Crash";
import FiltersProvider from "./views/Prepration/SalPlan/containers/FiltersContext";
import { Font } from "@react-pdf/renderer";
import theme from "./theme";

import { MEDIA_URL } from "./api";

const queryClient = new QueryClient();
const Router = BrowserRouter as unknown as React.FC<React.PropsWithChildren>;

// Component to restore global state from localStorage
const StateRestoration = () => {
  const dispatch = useDispatch();

  useEffect(() => {
    // Restore intention state if it exists
    const storedMessage = window.localStorage.getItem("intentionMessage");
    const storedStartDate = window.localStorage.getItem("intentionStartDate");
    
    if (storedMessage && storedStartDate) {
      dispatch(setMessage({ value: storedMessage }));
      dispatch(startDateValue({ value: storedStartDate }));
      dispatch(toggleIsBtn({ value: true }));
      dispatch(toggleBlinkValue({ value: true }));
    }
  }, [dispatch]);

  return null; // This component only handles state restoration
};

const initialContext: {
  background: {
    get: string;
    set: Dispatch<SetStateAction<string>>;
  };
} = {
  background: {
    get: "#fff",
    set: () => {},
  },
};
export const AppContext = React.createContext(initialContext);
export const useAppContext = () => useContext(AppContext);
const Providers = ({ children }: { children: React.ReactNode }) => {
  const [background, setBackground] = useState("#fff");
  const [accepted, setAccepted] = useState(
    window.localStorage.getItem("__sal_ca") === "1"
  );

  const [analyticsAccepted, setAnalyticsAccepted] = useState(
    window.localStorage.getItem("__sal_analytics_consent") !== null
  );

  const store = {
    background: {
      get: background,
      set: setBackground,
    },
  };

  useEffect(() => {
    // this font related to focus poster
    Font.register({
      family: "Droid",
      src: `${MEDIA_URL}/fonts/1625044265876-160603289.ttf`,
    });
  }, []);

  return (
    <CrashWrapper>
      <ReduxProvider store={reduxStore}>
        <StateRestoration />
        <AppContext.Provider value={store}>
          <QueryClientProvider client={queryClient}>
            <Router>
              <ChakraProvider theme={theme}>
                <LocaleProvider>
                  <FiltersProvider>
                    {(!navigator.cookieEnabled ||
                      window.localStorage.getItem("__sal_ca") !== "1") &&
                      !accepted && (
                        <CookieChecker accept={() => setAccepted(true)} />
                      )}
                    {accepted &&
                      window.localStorage.getItem("__sal_analytics_consent") ===
                        null && (
                        <CookieChecker
                          accept={() => setAnalyticsAccepted(true)}
                          analyticsOnly
                        />
                      )}
                    <MediaBox>{children}</MediaBox>
                  </FiltersProvider>
                </LocaleProvider>
              </ChakraProvider>
            </Router>
            <ReactQueryDevtools />
          </QueryClientProvider>
        </AppContext.Provider>
      </ReduxProvider>
    </CrashWrapper>
  );
};

export default Providers;
